import { useEffect, useState, useCallback, useMemo } from "react";
import { useDashboard } from "../../../contexts/DashboardContext";
import { useAccount } from "../../../contexts/AccountContext";
import { useCase } from "../../../contexts/CaseContext";
import { useParent } from "../../../contexts/ParentContext";
import { useChild } from "../../../contexts/ChildContext";
import { TimeFrame } from "../types";
import { getAgeGroupFromDob, RACE_LABELS, Race, AgeGroup, AGE_GROUP_LABELS } from "@visitproof/types";

export interface DemographicSummary {
  label: string;
  value: string;
  percentage: number;
}

export interface DashboardDemographics {
  totalChildren: number;
  topRace: DemographicSummary | null;
  topAgeGroup: DemographicSummary | null;
  sexDistribution: { male: number; female: number };
}

export const useDashboardDetail = () => {
  const { cases } = useCase();
  const { parents } = useParent();
  const { childList } = useChild();
  const { account } = useAccount();
  const [timeFrame, setTimeFrame] = useState<TimeFrame>("last_week");

  const { visits, dashboardData, reloadDashboards, loading, error } = useDashboard();

  // Memoized currency formatter
  const formatter = useMemo(() => new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }), []);

  // Memoized dashboard data to prevent unnecessary re-renders
  const memoizedDashboardData = useMemo(() => dashboardData, [dashboardData]);

  // Demographics summary for dashboard
  const demographicsSummary = useMemo((): DashboardDemographics => {
    const totalChildren = childList.length;

    // Count races
    const raceCounts: Record<string, number> = {};
    childList.forEach((child) => {
      if (child.race) {
        raceCounts[child.race] = (raceCounts[child.race] || 0) + 1;
      }
    });

    // Find top race
    let topRace: DemographicSummary | null = null;
    const raceEntries = Object.entries(raceCounts);
    if (raceEntries.length > 0) {
      const [topRaceKey, topRaceCount] = raceEntries.sort((a, b) => b[1] - a[1])[0];
      const childrenWithRace = Object.values(raceCounts).reduce((a, b) => a + b, 0);
      topRace = {
        label: "Most Common Race",
        value: RACE_LABELS[topRaceKey as Race] || topRaceKey,
        percentage: childrenWithRace > 0 ? Math.round((topRaceCount / childrenWithRace) * 100) : 0,
      };
    }

    // Count age groups
    const ageGroupCounts: Record<AgeGroup, number> = {
      "0-2": 0, "3-5": 0, "6-8": 0, "9-11": 0, "12-14": 0, "15-17": 0, "18+": 0,
    };
    childList.forEach((child) => {
      if (child.dob) {
        const ageGroup = getAgeGroupFromDob(child.dob.toDate());
        ageGroupCounts[ageGroup]++;
      }
    });

    // Find top age group
    let topAgeGroup: DemographicSummary | null = null;
    const ageEntries = Object.entries(ageGroupCounts).filter(([, count]) => count > 0);
    if (ageEntries.length > 0) {
      const [topAgeKey, topAgeCount] = ageEntries.sort((a, b) => b[1] - a[1])[0];
      const childrenWithAge = Object.values(ageGroupCounts).reduce((a, b) => a + b, 0);
      topAgeGroup = {
        label: "Most Common Age Group",
        value: AGE_GROUP_LABELS[topAgeKey as AgeGroup] || topAgeKey,
        percentage: childrenWithAge > 0 ? Math.round((topAgeCount / childrenWithAge) * 100) : 0,
      };
    }

    // Sex distribution
    let male = 0;
    let female = 0;
    childList.forEach((child) => {
      if (child.sex === "male") male++;
      else if (child.sex === "female") female++;
    });

    return {
      totalChildren,
      topRace,
      topAgeGroup,
      sexDistribution: { male, female },
    };
  }, [childList]);

  // Callback for time frame changes
  const handleTimeFrameChange = useCallback((newTimeFrame: TimeFrame) => {
    setTimeFrame(newTimeFrame);
  }, []);

  // Effect for reloading dashboard data
  useEffect(() => {
    if (timeFrame) {
      reloadDashboards(timeFrame);
    }
  }, [timeFrame, reloadDashboards]);

  return {
    visits,
    cases,
    parents,
    dashboardData: memoizedDashboardData,
    demographicsSummary,
    formatter,
    timeFrame,
    loading,
    error,
    handleTimeFrameChange,
    account,
    reloadDashboards,
  };
}; 